import { AuthApiAdapter } from '../adapters/AuthApiAdapter';
import {
    ForgotPasswordRequest,
    LoginRequest,
    ResetPasswordRequest,
    User
} from '../models/Auth';

export class AuthService {
    constructor(private apiAdapter: AuthApiAdapter) { }

    // 🎯 LOGIN
    async login(credentials: LoginRequest): Promise<{ token: { token: string; type?: string; expires_at?: string } }> {
        // 🎯 Validaciones de negocio
        if (!credentials.email || !credentials.email.trim()) {
            throw new Error('El email es requerido');
        }

        if (!credentials.password || !credentials.password.trim()) {
            throw new Error('La contraseña es requerida');
        }

        // Validar formato de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(credentials.email)) {
            throw new Error('El formato del email no es válido');
        }

        // Preparar credenciales limpiando espacios
        const cleanCredentials: LoginRequest = {
            email: credentials.email.trim().toLowerCase(),
            password: credentials.password
        };

        try {
            return await this.apiAdapter.login(cleanCredentials);
        } catch (error: any) {
            // 🎯 Transformar errores de API a errores de negocio
            if (error.message?.includes('User not found')) {
                throw new Error('Usuario no encontrado');
            }
            if (error.message?.includes('Wrong password')) {
                throw new Error('Contraseña incorrecta');
            }
            if (error.message?.includes('Unauthorized')) {
                throw new Error('Credenciales inválidas');
            }
            throw error;
        }
    }

    // 🎯 LOGOUT
    async logout(): Promise<void> {
        try {
            await this.apiAdapter.logout();
        } catch (error) {
            // 🎯 El logout siempre debe funcionar, incluso si hay error en API
            console.warn('Error al hacer logout en servidor:', error);
        }
    }

    // 🎯 CURRENT USER
    async getCurrentUser(): Promise<User> {
        return await this.apiAdapter.getCurrentUser();
    }

    // 🎯 REQUIRIR RESET DE CONTRASEÑA
    async requireResetPassword(request: ForgotPasswordRequest): Promise<void> {
        // 🎯 Validaciones de negocio
        if (!request.email || !request.email.trim()) {
            throw new Error('El email es requerido');
        }

        // Validar formato de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(request.email)) {
            throw new Error('El formato del email no es válido');
        }

        // Preparar email limpiando espacios
        const cleanRequest: ForgotPasswordRequest = {
            email: request.email.trim().toLowerCase()
        };

        try {
            return await this.apiAdapter.requireResetPassword(cleanRequest);
        } catch (error: any) {
            // 🎯 Transformar errores de API a errores de negocio
            if (error.message?.includes('User not found')) {
                throw new Error('Usuario no encontrado');
            }
            throw error;
        }
    }

    // 🎯 CONFIRMAR RESET DE CONTRASEÑA
    async confirmResetPassword(request: ResetPasswordRequest): Promise<void> {
        // 🎯 Validaciones de negocio
        if (!request.email || !request.email.trim()) {
            throw new Error('El email es requerido');
        }

        if (!request.password || !request.password.trim()) {
            throw new Error('La contraseña es requerida');
        }

        if (!request.token || !request.token.trim()) {
            throw new Error('El token es requerido');
        }

        // Validar formato de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(request.email)) {
            throw new Error('El formato del email no es válido');
        }

        // Preparar datos limpiando espacios
        const cleanRequest: ResetPasswordRequest = {
            email: request.email.trim().toLowerCase(),
            password: request.password,
            token: request.token.trim()
        };

        try {
            return await this.apiAdapter.confirmResetPassword(cleanRequest);
        } catch (error: any) {
            // 🎯 Transformar errores de API a errores de negocio
            if (error.message?.includes('Invalid token')) {
                throw new Error('Token inválido o expirado');
            }
            if (error.message?.includes('User not found')) {
                throw new Error('Usuario no encontrado');
            }
            throw error;
        }
    }

    // 🎯 CAMBIAR DE TEAM
    async switchTeam(teamId: number): Promise<{
        token: string;
        current_team_id: number;
        current_role_id: number;
        expires_at?: string;
    }> {
        // 🎯 Validaciones de negocio
        if (!teamId || teamId <= 0) {
            throw new Error('El ID del team es requerido');
        }

        try {
            return await this.apiAdapter.switchTeam(teamId);
        } catch (error: any) {
            // 🎯 Transformar errores de API a errores de negocio
            if (error.message?.includes('Team not found')) {
                throw new Error('Team no encontrado');
            }
            if (error.message?.includes('Unauthorized')) {
                throw new Error('No tienes permiso para cambiar a este team');
            }
            throw error;
        }
    }
} 
